/**
 * Pure email-HTML content-width parsing, shared by the server screenshot
 * pipeline (`lib/rendering/email-screenshot.ts`) and client surfaces that
 * render stored email HTML (/explore viewer cards, template gallery
 * iframes, read-only preview frames). Client-safe: no server-only imports.
 */

const DEFAULT_EMAIL_HTML_WIDTH = 600
// Sanity bounds for widths parsed out of email HTML — anything outside this
// range is a parse artifact, not a real email canvas.
const MIN_PARSED_EMAIL_HTML_WIDTH = 320
const MAX_PARSED_EMAIL_HTML_WIDTH = 1400
// Runtime-measured widths come from a live layout, so they get a wider
// ceiling than the parse heuristic, but still a bound against runaway
// documents (a single unbroken data-URI line can be tens of thousands of
// pixels wide).
const MAX_MEASURED_EMAIL_WIDTH = 1600
// A measured width must beat the current width by more than this to be
// adopted upward (sub-pixel scroll rounding), and undershoot by more than
// the hysteresis to be adopted downward (avoids relayout ping-pong between
// a parse guess and a probe that landed a few pixels narrower).
const MEASURED_UP_TOLERANCE_PX = 1
const MEASURED_DOWN_HYSTERESIS_PX = 8

const STYLE_ATTR_RE = /style\s*=\s*(?:"([^"]*)"|'([^']*)')/gi
const MAX_WIDTH_DECL_RE = /(?:^|;)\s*max-width\s*:\s*([\d.]+)\s*(px|em)\b/i
const TABLE_TAG_RE = /<table\b[^>]*>/gi
const WIDTH_ATTR_RE =
  /(?<![-\w])width\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s"'=<>`]+))/i
const WIDTH_DECL_RE = /(?:^|;)\s*width\s*:\s*([\d.]+)\s*px\b/i
// Lazy comment matcher: removes whole `<!--[if mso]>…<![endif]-->` blocks
// (Outlook-only shells whose widths never render in a browser/preview) while
// leaving downlevel-revealed content intact — `<!--[if !mso]><!-->` and its
// closing `<!--<![endif]-->` are each a complete comment on their own, so the
// revealed markup between them survives the strip.
const HTML_COMMENT_RE = /<!--[\s\S]*?-->/g

function parseCandidate(raw: string | undefined): number | null {
  if (!raw) {
    return null
  }
  const value = raw.trim()
  if (value.length === 0 || value.includes('%')) {
    return null
  }
  const parsed = Number.parseFloat(value)
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return null
  }
  return parsed
}

/**
 * Parse the email's content width (px) from its rendered HTML, or `null`
 * when nothing parseable is found.
 *
 * Two candidate families, resolved together:
 *
 * - react-email's `<Container>` renders `width:100%` + `max-width:<value>`
 *   inline, so the FIRST `max-width` carried by an inline `style` attribute
 *   in document order is the outermost container's width (the wrapping
 *   element serializes before its children). Only inline `style="…"`
 *   attributes are inspected: the injected responsive `<style>` block
 *   contains `@media (max-width: 480px)` text that a whole-document scan
 *   would misread. Only px and em (react-email's default is `37.5em`) are
 *   real canvas widths; percent values are skipped.
 * - Imported/raw-table emails declare geometry on `<table>` tags instead —
 *   `width="700"` / `width="600px"` attributes or `width:670px` inline
 *   declarations — often with a NARROWER inner `max-width` (a capped image)
 *   that the first-match heuristic would wrongly crown. Table candidates are
 *   tag-scoped so `img width="15px"` / `td width="144"` icon and column
 *   sizes never register; the widest fixed table is the real canvas (any
 *   fixed table that wide overflows a narrower frame).
 *
 * The result is the larger of the two families. Values under the sanity
 * floor are skipped rather than returned: emails can lead with a small
 * element (a logo img with `max-width: 200px`) — keep scanning instead of
 * shrinking the whole email to it. Outlook-conditional (`<!--[if mso]>`)
 * markup is stripped first: its widths never render outside Outlook.
 */
export function parseEmailHtmlContentWidth(html: string): number | null {
  const visible = html.replace(HTML_COMMENT_RE, '')

  let containerMaxWidth: number | null = null
  STYLE_ATTR_RE.lastIndex = 0
  let styleMatch: RegExpExecArray | null
  while ((styleMatch = STYLE_ATTR_RE.exec(visible)) !== null) {
    const style = styleMatch[1] ?? styleMatch[2] ?? ''
    const widthMatch = MAX_WIDTH_DECL_RE.exec(style)
    if (!widthMatch) {
      continue
    }
    const raw = Number.parseFloat(widthMatch[1])
    if (!Number.isFinite(raw) || raw <= 0) {
      continue
    }
    const px = Math.round(widthMatch[2].toLowerCase() === 'em' ? raw * 16 : raw)
    if (px < MIN_PARSED_EMAIL_HTML_WIDTH) {
      continue
    }
    containerMaxWidth = px
    break
  }

  let widestTable: number | null = null
  TABLE_TAG_RE.lastIndex = 0
  let tableMatch: RegExpExecArray | null
  while ((tableMatch = TABLE_TAG_RE.exec(visible)) !== null) {
    const tag = tableMatch[0]
    const attrMatch = WIDTH_ATTR_RE.exec(tag)
    const attrWidth = parseCandidate(
      attrMatch?.[1] ?? attrMatch?.[2] ?? attrMatch?.[3]
    )
    const styleAttr = /style\s*=\s*(?:"([^"]*)"|'([^']*)')/i.exec(tag)
    const declWidth = parseCandidate(
      WIDTH_DECL_RE.exec(styleAttr?.[1] ?? styleAttr?.[2] ?? '')?.[1]
    )
    for (const candidate of [attrWidth, declWidth]) {
      if (candidate === null) {
        continue
      }
      const px = Math.round(candidate)
      if (px < MIN_PARSED_EMAIL_HTML_WIDTH) {
        continue
      }
      if (widestTable === null || px > widestTable) {
        widestTable = px
      }
    }
  }

  const resolved = Math.max(containerMaxWidth ?? 0, widestTable ?? 0)
  if (resolved === 0) {
    return null
  }
  return Math.min(MAX_PARSED_EMAIL_HTML_WIDTH, resolved)
}

/**
 * Resolve the screenshot capture viewport width from the rendered email HTML,
 * falling back to the historical 600 when nothing parseable is found.
 *
 * The TipTap viewer/editor size themselves from the parsed `<Container>`
 * width (see lib/rendering/email-tiptap-viewer/container-width.ts, which
 * explicitly supports non-600 layouts like Meta's 680px templates), but the
 * PNG was historically captured at a hard-coded 600px — a 680px container
 * reflowed to 600 in the stored preview and no longer matched what the user
 * sees on the canvas. Capturing at the parsed width keeps the stored PNG's
 * logical width equal to the email's true content width.
 */
export function resolveScreenshotWidthFromHtml(html: string): number {
  return parseEmailHtmlContentWidth(html) ?? DEFAULT_EMAIL_HTML_WIDTH
}

/**
 * Decide whether a runtime-measured document width should replace the
 * current (parsed or defaulted) width.
 *
 * The live measurement is ground truth when it is decisive: adopt upward
 * past sub-pixel tolerance (content wider than the frame is otherwise
 * silently clipped — `scrolling="no"`), adopt downward only past a
 * hysteresis band (a few pixels of undershoot is scroll rounding, not a
 * narrower email). Measurements under the sanity floor mean the document is
 * fluid (it collapsed under a max-content probe) and carry no width signal —
 * keep the fallback.
 */
export function adoptMeasuredEmailWidth(
  measured: number,
  fallback: number
): number {
  if (!Number.isFinite(measured)) {
    return fallback
  }
  const rounded = Math.round(measured)
  if (rounded < MIN_PARSED_EMAIL_HTML_WIDTH) {
    return fallback
  }
  if (
    rounded > fallback + MEASURED_UP_TOLERANCE_PX ||
    rounded < fallback - MEASURED_DOWN_HYSTERESIS_PX
  ) {
    return Math.min(MAX_MEASURED_EMAIL_WIDTH, rounded)
  }
  return fallback
}
